import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { Highlight } from '@wf-common/atoms'

import SearchField from '../searchfield/searchfield'
import VirtualList from '../virtual-list/virtual-list'
import virtualListPropType from '../virtual-list/virtual-list-prop-type'

import styles from './filterable-list.module.css'

const propTypes = {
    className: PropTypes.string,
    filter: PropTypes.func,
    filterBy: PropTypes.string,
    filterText: PropTypes.string,
    findScrollPosition: PropTypes.func,
    highlight: PropTypes.func,
    listClassName: PropTypes.string,
    maxItems: PropTypes.number,
    noResultNode: PropTypes.node,
    placeholder: PropTypes.string,
    virtual: virtualListPropType
}

const defaultProps = {
    filter(value, item) {
        return (
            !value ||
            !(typeof item.props[this.filterBy] === 'string') ||
            item.props[this.filterBy].toLowerCase().includes(value)
        )
    },

    filterText: '',

    highlight(value, item) {
        const text = item.props[this.filterBy]

        return React.cloneElement(item, {
            children: <Highlight keyword={value} text={text} />
        })
    }
}

/**
 * A list that adds a text input before rendering children. The children are filtered
 * using the provided filter function (or default item === filterTextValue function).
 *
 * There is a default type of list items (in the 'list' component) <ListItem>Name</ListItem>.
 *
 * Usage:
 *
 * @example
 * <FilterableList filterBy="propNameInChildElements" maxItems={999}>
 *     <ListItem propNameInChildElements="text that will be used for matching">
 *     ...
 * </FilterableList>
 *
 */
export default class FilterableList extends Component {
    static updateScrollPosition(children, virtual) {
        if (virtual === null || typeof virtual !== 'object' || virtual.scrollPosition !== undefined) {
            return virtual
        }

        const scrollPosition = virtual.findScrollPosition ? virtual.findScrollPosition(children) : 0

        return {
            ...virtual,
            scrollPosition
        }
    }

    constructor(props) {
        super(props)

        this.onFilter = this.onFilter.bind(this)
        this.state = {
            filterText: props.filterText
        }
        this.filterBy = props.filterBy || 'name'
    }

    render() {
        const { children, className, listClassName, placeholder, virtual } = this.props
        const filterClassName = cx(styles['filterable-list'], className)
        const filterListClassName = cx(
            styles['list'],
            {
                [styles['virtual-list']]: virtual
            },
            listClassName
        )
        const { filterText } = this.state

        return (
            <div className={filterClassName}>
                <SearchField
                    className="t3sel-filterable-list-filter"
                    value={filterText}
                    onChange={this.onFilter}
                    containerClassName={styles['textfield']}
                    placeholder={placeholder}
                />
                <div className={filterListClassName}>{this.renderFilteredChildren(children, virtual)}</div>
            </div>
        )
    }

    onFilter(value) {
        this.setState({
            filterText: value || ''
        })
    }

    renderFilteredChildren(children, virtual) {
        const filterValue = this.state.filterText.trim().toLowerCase()
        const { filter, highlight, noResultNode } = this.props
        let filteredChildren = children.filter(filter.bind(this, filterValue))
        if (this.props.maxItems) {
            filteredChildren = filteredChildren.slice(0, this.props.maxItems)
        }
        const filteredHighlightedChildren = filteredChildren.map(highlight.bind(this, filterValue))

        // when something was entered and there are no results, show the passed component which indicates
        // an empty result set
        const hasChildren = filteredChildren && filteredChildren.length > 0
        const showNoDataIndicator = !hasChildren && filterValue !== '' && noResultNode

        if (showNoDataIndicator) {
            return noResultNode
        }

        // find scrollPosition if not set
        const virtualWithScrollPosition = FilterableList.updateScrollPosition(filteredChildren, virtual)

        return virtual ? (
            <VirtualList settings={virtualWithScrollPosition}>{filteredHighlightedChildren}</VirtualList>
        ) : (
            filteredHighlightedChildren
        )
    }

    reset() {
        this.setState({
            filterText: ''
        })
    }
}

FilterableList.propTypes = propTypes
FilterableList.defaultProps = defaultProps
